import React, { useRef, useMemo } from 'react';
import type { TimeBin } from './KalenderwocheBarChart';
import type { PrimaryAxisVolumeBin } from '../../../services/api';
import { useEChartsLifecycle, STD_TOOLTIP_STYLE } from './_chartCommon';
import { CATEGORY_KEYS } from '../../../constants';

// <purpose>Quartal.Verlauf — 3-level sunburst (Year/Quartal/Category) following the
// ECharts books-example pattern: explicit itemStyle.color on EVERY node + per-level
// levels[] config + solid white borders. Navy-tint gray palette throughout.</purpose>
// <constraints>
//   binLabel "Q{q}/{yy}" — year extracted as "20"+yy.
//   currentQ = last bin chronologically; its year + own node + K children get denser
//   navy tints than peers.
//   Solid hex on every node — mixing explicit colors with auto-shade descendants
//   broke drill interpolation; rgba alphas had the same effect.
//   Native nodeClick:'rootToNode'. No click handler — partial setOption inside click
//   resets drill state.
// </constraints>

type Props = {
  data: TimeBin[];
  rawCategoryData?: PrimaryAxisVolumeBin[];
  filterHash?: string;
};

function parseQuartalLabel(label: string): { quarter: number; year: string } | null {
  const m = label.match(/^Q(\d)\/(\d{2})$/);
  if (!m) return null;
  return { quarter: parseInt(m[1], 10), year: '20' + m[2] };
}

// Solid hex equivalents of the navy-tint alphas (computed against white). Solid hex
// is required — rgba breaks ECharts' color interpolation during drill.
const PALETTE = {
  yearCurrent: '#000d66',
  yearPast: '#394388',
  qCurrent: '#c7cadc',
  qPeer: '#dddee5',
  kCurrent: '#e5e6f0',
  kPeer: '#eff0f5',
  border: '#ffffff',
  label: '#000d66',
};

const CAT_ORDER = [...CATEGORY_KEYS];

export const QuartalVerlauf: React.FC<Props> = ({ data, rawCategoryData, filterHash }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const kPerBin = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    if (rawCategoryData?.length) {
      for (const b of rawCategoryData) {
        if (!map.has(b.binLabel)) map.set(b.binLabel, {});
        const entry = map.get(b.binLabel)!;
        entry[b.primaryAxis] = (entry[b.primaryAxis] ?? 0) + b.count;
      }
    }
    return map;
  }, [rawCategoryData]);

  useEChartsLifecycle(containerRef, chart => {
    const NAVY = PALETTE.label;
    const currentQLabel = data.length > 0 ? data[data.length - 1].label : null;
    const currentYear = currentQLabel ? parseQuartalLabel(currentQLabel)?.year ?? null : null;

    const yearMap = new Map<string, TimeBin[]>();
    for (const bin of data) {
      const parsed = parseQuartalLabel(bin.label);
      if (!parsed) continue;
      const yearKey = parsed.year;
      if (!yearMap.has(yearKey)) yearMap.set(yearKey, []);
      yearMap.get(yearKey)!.push(bin);
    }

    const sunburstData = Array.from(yearMap.entries()).map(([year, bins]) => {
      const isCurrentYear = year === currentYear;
      return {
        name: year,
        itemStyle: { color: isCurrentYear ? PALETTE.yearCurrent : PALETTE.yearPast },
        children: bins.map(bin => {
          const isCurrentQ = bin.label === currentQLabel;
          const kCounts = kPerBin.get(bin.label);
          const parsed = parseQuartalLabel(bin.label);
          const quartName = parsed ? `Q${parsed.quarter}` : bin.label;
          const children = kCounts
            ? CAT_ORDER
                .filter(k => (kCounts[k] ?? 0) > 0)
                .map(k => ({
                  name: k,
                  value: kCounts[k],
                  itemStyle: { color: isCurrentQ ? PALETTE.kCurrent : PALETTE.kPeer },
                }))
            : [];
          return {
            name: quartName,
            value: bin.count,
            itemStyle: { color: isCurrentQ ? PALETTE.qCurrent : PALETTE.qPeer },
            children,
          };
        }),
      };
    });

    chart.setOption({
      tooltip: {
        ...STD_TOOLTIP_STYLE,
        textStyle: { color: NAVY, fontSize: 11 },
        formatter: (p: any) => {
          const path = p.treePathInfo
            ? p.treePathInfo.map((t: any) => t.name).filter((n: string) => n).join(' › ')
            : p.name;
          return `<div style="font-size:10px;color:#888">${path}</div><div style="font-size:14px;color:${NAVY};font-weight:600">${p.value} <span style="font-size:10px;font-weight:400;color:#888">Tickets</span></div>`;
        },
      },
      series: [{
        type: 'sunburst',
        data: sunburstData,
        center: ['50%', '50%'],
        sort: null,
        itemStyle: { borderColor: PALETTE.border, borderWidth: 2, borderRadius: 4 },
        label: { color: NAVY, fontSize: 10, fontWeight: 600 },
        levels: [
          // Synthetic root / back-disc. Year ring's r0:'10%' leaves space so this disc
          // stays visible at both root view (passive marker) and drilled view (back target).
          { itemStyle: { color: '#eef0f5', borderColor: '#ffffff', borderWidth: 2 }, label: { show: false } },
          // Year ring
          { r0: '10%', r: '36%', label: { rotate: 0, fontSize: 12, color: '#ffffff', fontWeight: 700 } },
          // Quartal ring
          { r0: '36%', r: '64%', label: { rotate: 'tangential', color: NAVY, fontSize: 10, fontWeight: 600 } },
          // Category ring — thin band, labels radially outside in the 64-100% margin
          { r0: '68%', r: '74%', label: { position: 'outside', rotate: 'radial', color: NAVY, fontSize: 9, padding: 2 } },
        ],
      }],
    });
  }, [data, kPerBin, filterHash]);

  return <div ref={containerRef} style={{ width: '100%', height: 260 }} />;
};
